'use client'
import React, { FC, ReactElement } from 'react'
import axios from 'axios'
import useTokenOutpoints from '@/components/search-input/hooks/useTokenOutpoints'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { formatUsdPer } from '@/utils/format-usd'
import { useToast } from '@/components/ui/use-toast'
import { CircleHelp } from 'lucide-react'
import Wallet from '@/utils/wallet'
import { API_URL, NETWORK, NETWORK_CONFIG } from '@/utils/constants'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'

import useSelect from '../../hooks/useSelect'
import Tabs from './tabs'
import Invoice from '../../components/invoice'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const POSTAGE = (NETWORK_CONFIG as any).postage

const CollectionDetailInscribeTransfer: FC<{
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
}> = (props): ReactElement => {
	const { floorSats, onClose, args, tab, onChangeTab, token, classification } = props
	const { slug } = args
	const { toast } = useToast()

	//
	// Local State
	//

	const [split, setSplit] = React.useState(1)
	const [amount, setAmount] = React.useState(
		token?.available_balance ? parseFloat(token.available_balance) : 0
	)
	const [estimateData, setEstimateData] = React.useState<any>(null)
	const filesRef = React.useRef<File[]>([])
	const feeRateRef = React.useRef<number>(0)

	//
	// Remote Data
	//

	const { exchangeRate } = useExchangeRate()
	const { data } = useTokenOutpoints(token)

	const ids = React.useMemo(() => {
		if (!data || !Array.isArray(data)) {
			return []
		}

		return data.map((e) => e.outpoint)
	}, [data])

	// useSelect() run for its state subscription; none of its return values are
	// consumed by this panel in its current form.
	useSelect(ids)

	// Default amount to 100% when token data becomes available
	React.useEffect(() => {
		if (token?.available_balance && amount === 0) {
			requestAnimationFrame(() => {
				setAmount(parseFloat(token.available_balance))
			})
		}
	}, [amount, token.available_balance])

	//
	// Helpers
	//

	const buildFiles = React.useCallback(
		(dAmount: number, dSplit: number) => {
			const value = Math.floor(dAmount / dSplit)
			let diff = (dAmount / dSplit - value) * dSplit

			return new Array(dSplit).fill('').map((_, _i) => {
				let type = slug.split('-')[0]

				if (type.endsWith('20')) {
					type = `${type.split('20')[0]}-20`
				}

				let v = Math.floor(dAmount / dSplit)

				if (diff > 0) {
					diff = diff - 1
					v = v + 1
				}

				let ticker = slug.split('-')
				ticker.shift()
				ticker = ticker.join('-')

				const payload = JSON.stringify({
					p: type,
					op: type === 'tap' ? 'token-transfer' : 'transfer',
					tick: ticker,
					amt: `${v}`
				})

				return new File([payload], `transfer-${v}-${slug.split('-')[1]}.txt`, {
					type: 'text/plain'
				})
			})
		},
		[slug]
	)

	//
	// Renders
	//

	const handleChangeSplit = React.useCallback((v: any) => setSplit(v[0]), [])
	const handleChangeAmount = React.useCallback((v: any) => setAmount(v[0]), [])

	const handleBuildPsbt = React.useCallback(
		async (deps: any[], feeRate: number) => {
			const [dAmount, dSplit] = deps

			// Build files for size calculation only (not uploaded yet)
			const files = buildFiles(dAmount, dSplit)
			filesRef.current = files
			feeRateRef.current = feeRate

			// Estimate cost (no DB rows created)
			const { data: estimate } = await axios.post(`${API_URL}/inscribe/estimate`, {
				destination: Wallet.getTaprootAddress(),
				fee_rate: feeRate,
				postage: POSTAGE,
				file_sizes: files.map((f) => f.size),
				content_type: 'text/plain'
			})
			setEstimateData(estimate)

			// Return estimate-based fee (no upload yet)
			return { psbt: '', fee: BigInt(estimate.network_fee || 0) }
		},
		[buildFiles]
	)

	const handleSubmit = React.useCallback(
		async (_psbt: string) => {
			// Now actually upload, sign, and pay
			const psbt = await Wallet.getInscribe(
				filesRef.current.map((f) => ({ file: f })),
				feeRateRef.current,
				POSTAGE
			)

			const { rawtx } = await Wallet.signInscribe(psbt)

			const { data } = await axios.post(`${API_URL}/inscribe/pay`, { rawtx })

			toast({
				title: 'Inscription Submitted',
				description: `Transaction: ${data.txid?.slice(0, 8)}...`
			})

			onClose()
		},
		[onClose, toast]
	)

	// @ts-ignore
	const usdPer = (((amount / split) * floorSats) / 1e8) * exchangeRate
	const quantityPerLotValue = `${formatNumber(
		formatUsdPer(Math.floor(amount / split)).usd
	)} / $${formatNumber(formatUsdPer(usdPer).usd)}`
	const hasAvailable = React.useMemo(() => {
		const id = parseFloat(token.available_balance)
		return !isNaN(id) && id > 0
	}, [token])

	const hasTransferable = React.useMemo(() => {
		const id = parseFloat(token.transferable_balance)
		return !isNaN(id) && id > 0
	}, [token])

	const invoiceItems = React.useMemo(() => {
		const serviceFee =
			NETWORK === 'BTC' && estimateData
				? (estimateData.base_fee || 0) + (estimateData.size_fee || 0)
				: 0
		const otherFees = POSTAGE * split
		const formatFeeValue = (amount: number) =>
			`${formatSatoshis(amount)} ${NETWORK} / $${formatNumber(
				((amount / 1e8) * exchangeRate)?.toFixed(2)
			)}`
		const items: any[] = [
			{
				title: 'Postage',
				emphasize: false,
				valueFunc: ({ building }: any) => (building ? '--' : formatFeeValue(otherFees))
			}
		]

		if (NETWORK === 'BTC' && estimateData) {
			items.push({
				title: 'Service Fee',
				valueFunc: ({ building }: any) => (building ? '--' : formatFeeValue(serviceFee))
			})
		}

		items.push({ title: 'Network Fee' })
		items.push({
			title: 'Total Fee',
			emphasize: true,
			valueFunc: ({ building, fee }: any) =>
				building ? '--' : formatFeeValue(Number(fee) + serviceFee + otherFees)
		})

		return items
	}, [split, exchangeRate, estimateData])

	return (
		<>
			<div className="flex px-2 h-7 w-full justify-between items-center">
				<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
			</div>
			<div className="flex px-2 py-1.5 gap-4 items-center">
				<Tabs value={tab} onValueChange={onChangeTab} slug={slug} />
			</div>
			{!hasAvailable && hasTransferable && (
				<div className="text-xs flex flex-col items-center justify-center h-full mt-8 gap-4">
					<p>You've already split this token</p>
					<span
						className="text-violet-500 cursor-pointer"
						onClick={() => onChangeTab('list')}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(() => onChangeTab('list'))}
					>
						List now
					</span>
				</div>
			)}
			{!hasAvailable && !hasTransferable && (
				<p className="text-xs flex items-center justify-center h-full mt-8">
					You don't have any available balance to split.
				</p>
			)}
			{hasAvailable && (
				<>
					<div className="flex p-2 flex-col w-full gap-2">
						<p className="text-xs whitespace-nowrap">
							Number of tokens to split: {formatNumber(amount)}
						</p>
						<Slider
							defaultValue={[amount]}
							value={[amount]}
							max={token.available_balance}
							min={0}
							onValueChange={handleChangeAmount}
							className="[&_[role=slider]]:size-3.5"
						/>
					</div>
					<div>
						<div className="flex items-center gap-2 p-2">
							<div className="flex items-center gap-1 shrink-0">
								<p className="text-xs whitespace-nowrap">Number of Outputs:</p>
								<div className="relative group">
									<CircleHelp className="size-3 text-muted-foreground cursor-help" />
									<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-50 w-[200px] rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md">
										Use this slider to decide the number of outputs to create from the selected
										token clips.
									</div>
								</div>
							</div>
							<div className="flex-1">
								<Slider
									defaultValue={[split]}
									value={[split]}
									max={24}
									min={1}
									step={1}
									onValueChange={handleChangeSplit}
									className="[&_[role=slider]]:size-3.5"
								/>
							</div>
							<input
								type="number"
								min={1}
								max={24}
								value={split}
								onChange={(e) => {
									const val = parseInt(e.target.value, 10)
									if (!isNaN(val)) setSplit(Math.max(1, Math.min(24, val)))
								}}
								className="w-12 h-7 text-center text-xs font-medium text-foreground bg-background border border-border rounded focus:outline-none focus:border-border transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
							/>
						</div>
						<div className="mx-2 flex items-center justify-between pb-2">
							<p className="text-xs font-semibold text-foreground">Quantity Per Lot</p>
							<p className="text-xs font-semibold text-muted-foreground">{quantityPerLotValue}</p>
						</div>
					</div>
				</>
			)}
			<Invoice
				open={amount > 0}
				onSubmit={handleSubmit}
				onBuild={handleBuildPsbt}
				buildDeps={[amount, split]}
				action={`Split ${formatNumber(amount)} ${classification}`}
				items={invoiceItems}
			/>
		</>
	)
}

export default CollectionDetailInscribeTransfer
